import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  acknowledgePublicGuestWaiver,
  acknowledgeMembershipWaiver,
  cancelPublicResourceReservation,
  cancelPublicEventRegistration,
  completeMembershipCheckout,
  createMembershipBalanceCheckout,
  createMembershipPlanChangeCheckout,
  createMembershipRenewalCheckout,
  createMembershipSignupIntent,
  createPublicResourceReservation,
  createPublicLead,
  createPublicEventRegistration,
  getPublicEventRegistrationStatus,
  getMembershipApplicantStatus,
  getPublicChapters,
  getPublicEvents,
  getPublicMembershipPayments,
  getPublicRange,
  getPublicRangeMembership,
  getPublicRangeProfile,
  getPublicReservationEligibility,
  listPublicResourceReservations,
  redeemPublicGuestPass,
  requestPublicQualification,
  reschedulePublicResourceReservation,
  startMembershipBackgroundCheck,
  submitMembershipPrequalification,
  unsubscribePublicCommunication
} from "../../lib/rangegridApi";
import {
  LIBERTY_PUBLIC_HERO_URL,
  LIBERTY_PUBLIC_LOGO_URL,
  libertyFacilityLine,
  libertyRangeFacts,
  libertyRangeStory
} from "./liberty/libertyContent";
import { PublicEventsSection } from "./PublicEventsSection";
import { PublicHomePage } from "./PublicHomePage";
import { PublicMembershipInterestPage } from "./PublicMembershipInterestPage";
import { PublicSiteShell } from "./PublicSiteShell";
import { LibertyAboutPage } from "./liberty/LibertyAboutPage";
import {
  MemberPortalAccessGate,
  ApplicantStatusPanel,
  MemberPortalDashboard,
  MemberPortalDocumentsPanel,
  MemberPortalEventsPanel,
  MemberPortalPaymentsPanel,
  MemberPortalSupportPanel,
  MemberPortalUnavailablePanel,
  MemberPortalVisitsPanel,
  MemberWorkspaceShell,
  memberPortalActionSettings,
  memberPortalTabFromHash,
  memberPaymentStatusLabel,
  memberStatusLabel
} from "../member-portal";
import {
  applicationFormIsPublished,
  normalizeApplicationFormForClient
} from "../membership/applicationForm";
import {
  campaignAttributionLabel,
  defaultLeadForm,
  formatAddress,
  formatDateOnly,
  formatMoney,
  formatPublicEventDate,
  isChapterOrganization,
  isLibertyLongRange,
  publicCampaignAttribution,
  publicEventTimezone,
  workspaceEventNounPlural,
  workspaceMemberNoun,
  workspaceNoun
} from "./publicSiteUtils";
import "./public-site.css";
import "../member-portal/member-portal.css";

const EMPTY_LIST = Object.freeze([]);

function memberCheckoutRecoveryMessage(message = "") {
  if (!message || message.toLowerCase().includes("internal server error")) {
    return "Latest check: confirmation is not available yet. This usually clears after Stripe finishes processing.";
  }
  return `Latest check: ${message}`;
}

function publicEventPriceLabel(event = {}) {
  if (event.pricing?.memberIncluded || event.pricing?.includedForMembers || event.includedForMembers) {
    return "Included with membership";
  }
  const amountCents = Number(event.pricing?.amountCents || 0);
  if (amountCents <= 0) {
    return "Free";
  }
  return formatMoney(event.pricing);
}

function publicEventAvailabilityLabel(event = {}) {
  const labels = {
    not_open: "Registration not open",
    closed: "Registration closed",
    disabled: "Registration unavailable",
    ended: "Event ended",
    full: "Event is full"
  };
  return labels[event.registrationAvailability] || "";
}

function publicEventRegistrationWindowLabel(event = {}, timezone = "") {
  const opensAt = event.registrationPolicy?.opensAt;
  const closesAt = event.registrationPolicy?.closesAt;
  if (opensAt && closesAt) {
    return `Registration ${formatPublicEventDate(opensAt, timezone)} - ${formatPublicEventDate(closesAt, timezone)}`;
  }
  if (opensAt) {
    return `Registration opens ${formatPublicEventDate(opensAt, timezone)}`;
  }
  if (closesAt) {
    return `Registration closes ${formatPublicEventDate(closesAt, timezone)}`;
  }
  return "";
}

function publicEventPrimaryActionLabel(event = {}, {
  activeRegistration = false,
  availabilityLabel = "",
  busy = false,
  isAuthenticated = false,
  memberOnlyBlocked = false,
  eventAccessBlocked = false,
  currentMember = null,
  eventAccess = {}
} = {}) {
  if (activeRegistration) {
    return "Registered";
  }
  if (availabilityLabel) {
    return availabilityLabel;
  }
  if (!isAuthenticated) {
    return "Sign in to register";
  }
  if (busy) {
    return "Registering...";
  }
  if (memberOnlyBlocked) {
    return currentMember ? "Activation needed" : "Membership needed";
  }
  if (eventAccessBlocked) {
    if (!currentMember) {
      return "Membership needed";
    }
    return eventAccess.pendingKeys?.length > 0 ? "Qualification pending" : "Qualification needed";
  }
  if (event.registrationAvailabilityDetail?.status === "waitlist") {
    return "Join waitlist";
  }
  return Number(event.pricing?.amountCents || 0) > 0 ? "Checkout" : "Register";
}

function publicEventRegistrationStatusLabel(status) {
  const labels = {
    confirmed: "Confirmed",
    waitlisted: "On the waitlist",
    payment_pending: "Payment pending",
    checkout_started: "Checkout started",
    cancelled: "Cancelled",
    attended: "Attended",
    no_show: "No-show"
  };
  return labels[status] || status || "Registered";
}

function publicEventAttendanceStatusLabel(status) {
  const labels = {
    attended: "Attended",
    no_show: "No-show",
    excused: "Excused"
  };
  return labels[status] || status || "Not recorded";
}

function planTermCopy(plan = {}) {
  if (plan.term?.unit === "lifetime" || plan.lifetime) {
    return "Lifetime";
  }
  if (plan.billing?.model === "one_time") {
    return "One-time";
  }
  const interval = plan.billing?.interval || plan.term?.unit;
  return interval ? `Renews ${interval}` : "Membership";
}

function publicSignupPlans(plans = []) {
  return plans.filter((plan) => (
    plan &&
    plan.publicSignupEnabled &&
    plan.status !== "inactive" &&
    plan.category !== "day_pass"
  ));
}

function PublicMembershipApplicationPage({
  busyMembershipTypeId = "",
  currentMember,
  leadFallback,
  memberPortalHref,
  onStartMembership,
  plans = [],
  workspaceLabel = "range"
}) {
  const visiblePlans = publicSignupPlans(plans);
  if (!visiblePlans.length) {
    return leadFallback;
  }

  return (
    <section className="public-membership-application-page">
      <div className="public-membership-plans-header">
        <span className="public-section-kicker">Membership application</span>
        <h2>Start your Liberty Long Range application</h2>
        <p>Choose the membership path, sign in with ArmorySync, and complete the owner review form from your member portal.</p>
      </div>

      <div className="public-membership-plan-list">
        {visiblePlans.map((plan) => {
          const busy = busyMembershipTypeId === plan.membershipTypeId;
          const benefitItems = Array.isArray(plan.benefits?.items) ? plan.benefits.items : [];
          return (
            <article className="public-membership-plan-card" key={plan.membershipTypeId}>
              <div>
                <span>{planTermCopy(plan)}</span>
                <h3>{plan.name}</h3>
                {plan.description && <p>{plan.description}</p>}
              </div>
              <dl>
                <div>
                  <dt>Cost</dt>
                  <dd>{Number(plan.pricing?.amountCents || 0) > 0 ? formatMoney(plan.pricing) : "No online payment today"}</dd>
                </div>
                <div>
                  <dt>Review</dt>
                  <dd>Owner approval required</dd>
                </div>
              </dl>
              {benefitItems.length > 0 && (
                <ul>
                  {benefitItems.slice(0, 4).map((item) => (
                    <li key={item.key || item.label || item}>{item.label || item.name || item}</li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className="public-primary-action"
                disabled={busy}
                onClick={() => onStartMembership(plan)}
              >
                {busy ? "Starting..." : "Start application"}
              </button>
            </article>
          );
        })}
      </div>

      {currentMember ? (
        <div className="notice is-info">
          You already have a membership record for this {workspaceLabel}.{" "}
          <a href={`${memberPortalHref}#member-status`}>Open membership status</a>
        </div>
      ) : null}
    </section>
  );
}

function registrationLooksLikeOrientation(registration = {}) {
  const event = registration.event || {};
  const text = [event.type, event.title, event.description, event.registrationPolicy?.instructions]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return text.includes("orientation") || text.includes("range tour") || text.includes("safety briefing");
}

function eventLooksLikeOrientation(event = {}) {
  const text = [event.type, event.title, event.description, event.registrationPolicy?.instructions]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return event.type === "orientation" || text.includes("orientation") || text.includes("range tour") || text.includes("safety briefing");
}

const INTERNAL_DEMO_EVENT_COPY = /(?:chapter lead to owner|owner\/chapter|owner handoff|handoff note|workflow verification|participant notes|reaudit|demo|internal|test event|audit)/i;

function publicEventDetailText(text, fallback = "") {
  if (!text) {
    return "";
  }
  if (INTERNAL_DEMO_EVENT_COPY.test(text)) {
    return fallback;
  }
  return text;
}

function publicEventTitleText(title = "") {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) {
    return "Published class";
  }
  if (!INTERNAL_DEMO_EVENT_COPY.test(cleanTitle)) {
    return cleanTitle;
  }
  return cleanTitle
    .replace(/^Demo\s+/i, "")
    .replace(/\bHandoff Drill\b/i, "Member Skills Session")
    .replace(/\bLead Reaudit\b/i, "Chapter Practice Session")
    .replace(/\bLead Final\b/i, "Chapter Training Session")
    .replace(/\s+\d{4}-\d{2}-\d{2}(?:\s+\d+)?$/i, "")
    .trim() || "Chapter class";
}

function publicEventInstructorText(instructor = {}) {
  const name = publicEventDetailText(instructor.name, "Chapter instructor") || instructor.name || "";
  return [name, instructor.role].filter(Boolean).join(" / ");
}

function publicContactLooksInternal(value = "") {
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com|555[-\s.]?0|555[-\s.]?0147)/i.test(String(value || ""));
}

function publicAddressLooksInternal(value = "") {
  return /(?:100\s+Chapter\s+House\s+Way|Los Angeles,\s*CA,\s*90012|Example|Demo|Test)/i.test(String(value || ""));
}

function publicMemberSupportContact(range = {}) {
  const profile = range?.profile || {};
  const fallback = isChapterOrganization(range) && /lox\s*&\s*loaded/i.test(publicDisplayName(range) || "")
    ? {
        email: "info@loxandloaded.org",
        phone: "1 (334) 569-5623"
      }
    : {};
  const email = profile.contactEmail && !publicContactLooksInternal(profile.contactEmail)
    ? profile.contactEmail
    : fallback.email || "";
  const phone = profile.contactPhone && !publicContactLooksInternal(profile.contactPhone)
    ? profile.contactPhone
    : fallback.phone || "";
  return { email, phone };
}

function publicEventTypeLabel(event = {}) {
  if (eventLooksLikeOrientation(event)) {
    return "Orientation session";
  }
  const labels = {
    class: "Class",
    clinic: "Clinic",
    match: "Match",
    open_shoot: "Open shoot",
    private_event: "Private event",
    other: "Program"
  };
  return labels[event.type] || qualificationDisplayLabel(event.type || "Program");
}

function publicEventOrientationSummary(event = {}) {
  if (!eventLooksLikeOrientation(event)) {
    return null;
  }
  const qualificationKeys = event.accessPolicy?.grantedQualificationKeys || event.qualificationKeys || [];
  const includesLongRange = qualificationKeys.some((key) => String(key).toLowerCase().includes("1000") || String(key).toLowerCase().includes("long"));
  return includesLongRange
    ? "Range tour, safety rules, and optional long-range qualification."
    : "Range tour, safety rules, and member access onboarding.";
}

function publicReservationStatusLabel(status) {
  const labels = {
    confirmed: "Confirmed booking",
    standby: "Standby request",
    cancelled: "Cancelled",
    completed: "Completed",
    no_show: "No-show recorded"
  };
  return labels[status] || status || "Reservation";
}

function memberSubscriptionSummary(member = {}) {
  const plan = member.membershipPlan || {};
  const stripe = member.stripe || {};
  const billing = plan.billing || {};
  const status = stripe.subscriptionStatus || stripe.paymentStatus || "";
  const recurring = billing.model === "subscription" || stripe.mode === "subscription" || Boolean(status);
  const recurringTerm = !recurring &&
    billing.model !== "one_time" &&
    plan.category !== "day_pass" &&
    !plan.lifetime &&
    plan.term?.unit &&
    plan.term.unit !== "lifetime" &&
    Number(plan.pricing?.amountCents || 0) > 0;
  const cadence = recurring
    ? `Every ${billing.intervalCount && Number(billing.intervalCount) > 1 ? `${billing.intervalCount} ` : ""}${billing.interval || plan.term?.unit || "year"}${billing.intervalCount && Number(billing.intervalCount) > 1 ? "s" : ""}`
    : recurringTerm
      ? `Every ${plan.term?.count && Number(plan.term.count) > 1 ? `${plan.term.count} ` : ""}${plan.term.unit}${plan.term.count && Number(plan.term.count) > 1 ? "s" : ""}`
    : plan.term?.unit === "lifetime" || plan.lifetime
      ? "Lifetime"
      : "One-time";
  const nextBillingDate = stripe.currentPeriodEnd || member.membership?.renewalDate || member.membership?.currentTermEnd;
  return {
    recurring,
    statusLabel: status ? memberPaymentStatusLabel(status) : recurring ? "Subscription" : plan.pricing?.amountCents ? "Dues recorded" : "No balance due",
    cadence,
    nextBillingLabel: nextBillingDate
      ? `${stripe.cancelAtPeriodEnd ? "Access through" : recurring ? "Next billing" : "Term ends"} ${formatDateOnly(nextBillingDate)}`
      : plan.term?.unit === "lifetime" || plan.lifetime
        ? "No next billing"
        : "Next billing not recorded",
    cancelLabel: stripe.cancelAtPeriodEnd ? "Cancels at period end" : ""
  };
}

function memberEventDisplayTitle(event = {}) {
  const title = String(event.title || "").trim();
  if (/^Demo member range night \d{8,}$/i.test(title)) {
    return "Member range night";
  }
  return title || "Member event";
}

function memberEventDisplayDescription(event = {}, fallback = "Event details will be confirmed by the range team.") {
  const description = String(event.description || event.summary || event.details || "").trim();
  if (/verify the member events workspace/i.test(description)) {
    return "Evening range session for members to check in, practice, and review arrival details before attending.";
  }
  return description || fallback;
}

function publicDisplayName(range = {}) {
  return isLibertyLongRange(range) ? "Liberty Long Range" : range?.name || "Range membership";
}

function publicHeroKicker(range = {}, memberConsoleMode = false, membershipPageMode = false, aboutPageMode = false) {
  if (memberConsoleMode) {
    return "Member console";
  }
  if (aboutPageMode) {
    return "About Liberty Long Range";
  }
  if (isChapterOrganization(range)) {
    return membershipPageMode ? "Chapter membership" : "Chapter organization";
  }
  if (isLibertyLongRange(range)) {
    return membershipPageMode ? "Membership updates" : "Coming soon";
  }
  return membershipPageMode ? "Membership updates" : "Membership signup";
}

function publicHeroDescription(range = {}, fallback = "", membershipPageMode = false, aboutPageMode = false) {
  if (aboutPageMode) {
    return "A members-only shooting facility being built in Liberty, Texas for families, serious shooters, law enforcement, and the shooting sports community.";
  }
  if (isChapterOrganization(range)) {
    return membershipPageMode
      ? "Choose your home chapter and start the membership path for local classes and chapter rosters."
      : "A connected chapter organization for memberships, local classes, chapter leaders, and attendance records.";
  }
  if (isLibertyLongRange(range)) {
    if (membershipPageMode) {
      return "Send your contact information and Liberty Long Range will follow up before memberships open.";
    }
    return "";
  }
  return fallback;
}

const PUBLIC_CONTEXT_CACHE_PREFIX = "rangegrid:public-range:";

function publicContextCacheKey(slug) {
  return `${PUBLIC_CONTEXT_CACHE_PREFIX}${slug}`;
}

function readCachedPublicContext(slug) {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(publicContextCacheKey(slug));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedPublicContext(slug, context) {
  if (typeof window === "undefined" || !context) {
    return;
  }
  try {
    window.sessionStorage.setItem(publicContextCacheKey(slug), JSON.stringify(context));
  } catch {
    // Rendering should not depend on storage availability.
  }
}

function initialPublicContext(slug) {
  return readCachedPublicContext(slug);
}

function publicRangeFacts(range = {}) {
  if (isChapterOrganization(range)) {
    return [
      { label: "Access model", value: "Chapter membership" },
      { label: "Organization", value: "Local chapters" },
      { label: "Classes", value: "Included for members" },
      { label: "Leaders", value: "Chapter-based teams" }
    ];
  }
  if (isLibertyLongRange(range)) {
    return libertyRangeFacts(range);
  }
  return [
    { label: "Access model", value: "Membership based" },
    { label: "Status", value: "Range-managed access" },
    { label: "Reservations", value: "Member portal" },
    { label: "Payments", value: "Secure checkout" }
  ];
}

function publicRangeStory(range = {}) {
  if (isChapterOrganization(range)) {
    return [
      {
        title: "Local chapters",
        body: "Join through the chapter closest to you so leaders can manage rosters, class attendance, and member communication locally."
      },
      {
        title: "Membership includes classes",
        body: "Published member classes and chapter events can be tied to your home chapter while the organization keeps one central record."
      },
      {
        title: "Connected leadership",
        body: "Chapter leaders see the member and class information they need without manual spreadsheets or email handoffs."
      }
    ];
  }
  if (isLibertyLongRange(range)) {
    return libertyRangeStory(range);
  }
  return [
    {
      title: "Membership access",
      body: "Share your contact information so the range can follow up when membership details are ready."
    },
    {
      title: "Member portal",
      body: "After sign-in, your membership, waiver, reservations, events, and receipts stay scoped to this range."
    },
    {
      title: "Range approval",
      body: "Some ranges review new members before access becomes active."
    }
  ];
}


function publicSafetyAccent(range = {}) {
  return range?.branding?.safetyColor || (isLibertyLongRange(range) ? "#f05a28" : "");
}

const LIBERTY_LONG_RANGE_QUALIFICATION_KEY = "liberty_1000_yard_qualified";
const LIBERTY_LONG_RANGE_QUALIFICATION_LABEL = "1,000-yard qualified";

function isLongRangeQualificationKey(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes("liberty_1000_yard_qualified") ||
    normalized.includes("1000") ||
    normalized.includes("1,000") ||
    normalized.includes("long range") ||
    normalized.includes("long-range")
  );
}

function canonicalQualificationKey(value = "") {
  const cleaned = String(value || "").trim();
  if (!cleaned) {
    return "";
  }
  return isLongRangeQualificationKey(cleaned) ? LIBERTY_LONG_RANGE_QUALIFICATION_KEY : cleaned;
}

function qualificationKeyList(keys = [], fallback = "None") {
  const displayKeys = [...new Set(keys.map((key) => String(key || "").trim()).filter(Boolean))];
  return displayKeys.length > 0 ? displayKeys.join(", ") : fallback;
}

function qualificationDisplayLabel(value = "") {
  const cleaned = String(value || "").trim();
  if (!cleaned) {
    return "Qualification";
  }
  if (isLongRangeQualificationKey(cleaned)) {
    return LIBERTY_LONG_RANGE_QUALIFICATION_LABEL;
  }
  return cleaned
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function qualificationSetHas(set, key) {
  const cleaned = String(key || "").trim();
  return Boolean(cleaned) && (set.has(cleaned) || set.has(canonicalQualificationKey(cleaned)));
}

function memberQualificationSets(member = {}) {
  const now = Date.now();
  const active = new Set();
  const pending = new Set();
  (member?.qualifications?.items || []).forEach((qualification) => {
    const key = String(qualification.key || "").trim();
    if (!key) {
      return;
    }
    const canonicalKey = canonicalQualificationKey(key);
    const expiresAt = qualification.expiresAt ? Date.parse(qualification.expiresAt) : null;
    const expired = Number.isFinite(expiresAt) && expiresAt < now;
    if (qualification.status === "active" && !expired) {
      active.add(key);
      if (canonicalKey) {
        active.add(canonicalKey);
      }
    }
    if (qualification.status === "pending" && !expired) {
      pending.add(key);
      if (canonicalKey) {
        pending.add(canonicalKey);
      }
    }
  });
  return { active, pending };
}

function eventQualificationSummary(event = {}, member = null) {
  const requiredKeys = (event.accessPolicy?.requiredQualificationKeys || [])
    .map((key) => String(key || "").trim())
    .filter(Boolean);
  if (requiredKeys.length === 0) {
    return {
      requiredKeys: [],
      requiredLabels: [],
      missingKeys: [],
      missingLabels: [],
      pendingKeys: [],
      pendingLabels: [],
      allowed: true,
      label: ""
    };
  }

  const { active, pending } = memberQualificationSets(member || {});
  const pendingKeys = requiredKeys.filter((key) => !qualificationSetHas(active, key) && qualificationSetHas(pending, key));
  const missingKeys = requiredKeys.filter((key) => !qualificationSetHas(active, key) && !qualificationSetHas(pending, key));
  return {
    requiredKeys,
    requiredLabels: requiredKeys.map(qualificationDisplayLabel),
    missingKeys,
    missingLabels: missingKeys.map(qualificationDisplayLabel),
    pendingKeys,
    pendingLabels: pendingKeys.map(qualificationDisplayLabel),
    allowed: Boolean(member) && missingKeys.length === 0 && pendingKeys.length === 0,
    label: qualificationKeyList(requiredKeys.map(qualificationDisplayLabel))
  };
}

function formatEventCapacity(event = {}) {
  const availability = event.registrationAvailabilityDetail || {};
  const maxAttendees = Number(event.capacity?.maxAttendees || 0);
  const waitlistLimit = Number(event.capacity?.waitlistLimit || 0);
  if (maxAttendees <= 0) {
    return "Open registration";
  }
  if (availability.status === "waitlist") {
    const remaining = Number(availability.waitlistRemaining || 0);
    return remaining > 0
      ? `Confirmed spots full / ${remaining} waitlist spot${remaining === 1 ? "" : "s"} left`
      : "Confirmed spots full / waitlist full";
  }
  if (availability.status === "full") {
    return waitlistLimit > 0 ? "Confirmed spots and waitlist are full" : "Event is full";
  }
  if (availability.confirmedRemaining !== null && availability.confirmedRemaining !== undefined) {
    const remaining = Number(availability.confirmedRemaining || 0);
    return `${remaining} of ${maxAttendees} spot${remaining === 1 ? "" : "s"} left${waitlistLimit > 0 ? ` / ${waitlistLimit} waitlist` : ""}`;
  }
  if (waitlistLimit > 0) {
    return `${maxAttendees} spots / ${waitlistLimit} waitlist`;
  }
  return `${maxAttendees} spots`;
}

function requirementText(requirement = {}, fallback) {
  if (!requirement.required) {
    return "Not required";
  }
  return requirement.instructions || fallback;
}

function waiverIsRequired(waiver = {}) {
  return waiver.required !== false;
}

function waiverIsExpired(waiver = {}) {
  if (!waiver.applicantAcknowledgementExpiresAt) {
    return false;
  }
  const expiresAt = Date.parse(waiver.applicantAcknowledgementExpiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
}

function waiverRequiresSignature(waiver = {}) {
  return waiverIsRequired(waiver) && (waiver.status !== "completed" || waiverIsExpired(waiver));
}

const RELEASE_FORM_ACTION_LABEL = "Review and sign release form";

function waiverStatusCopy(waiver = {}) {
  if (!waiverIsRequired(waiver)) {
    return {
      label: "Not required",
      detail: "No release form is required for this membership.",
      signature: "Not required",
      actionLabel: "",
      expired: false
    };
  }
  if (waiverIsExpired(waiver)) {
    const expiresAt = formatDateOnly(waiver.applicantAcknowledgementExpiresAt, "Expired");
    return {
      label: "Expired",
      detail: `Your prior release form expired ${expiresAt}. Re-sign the current version before access stays current.`,
      signature: `Expired ${expiresAt}`,
      actionLabel: RELEASE_FORM_ACTION_LABEL,
      expired: true
    };
  }
  if (waiver.status !== "completed") {
    return {
      label: "Signature required",
      detail: requirementText(waiver, "Review and acknowledge the current range release form before access is active."),
      signature: "Signature required",
      actionLabel: RELEASE_FORM_ACTION_LABEL,
      expired: false
    };
  }
  const acceptedAt = formatDateOnly(waiver.applicantAcknowledgedAt, "Signed");
  const expiresAt = waiver.applicantAcknowledgementExpiresAt
    ? ` / expires ${formatDateOnly(waiver.applicantAcknowledgementExpiresAt)}`
    : "";
  return {
    label: "Signed",
    detail: waiver.applicantAcknowledgementExpiresAt
      ? `Signed ${acceptedAt}; expires ${formatDateOnly(waiver.applicantAcknowledgementExpiresAt)}.`
      : `Signed ${acceptedAt}.`,
    signature: `${acceptedAt}${expiresAt}`,
    actionLabel: "",
    expired: false
  };
}

function safeMemberAccountLabel(label = "") {
  const value = String(label || "");
  return (
    /^[0-9a-f-]{8}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{12}@dev\.rangegrid\.local$/i.test(value) ||
    /@dev\.rangegrid\.local$/i.test(value)
  )
    ? "Signed in"
    : label;
}


export function PublicRangePage({
  slug,
  isAuthenticated,
  memberAccount = null,
  memberConsoleMode = false,
  onMemberSignOut,
  onRequireAuth,
  eventCompletionEventId = "",
  eventRegistrationCompletionId = "",
  reservationCompletionId = ""
}) {
  const [context, setContext] = useState(() => initialPublicContext(slug));
  const [publicChapterDirectory, setPublicChapterDirectory] = useState({ enabled: false, chapters: [] });
  const [publicEvents, setPublicEvents] = useState({ enabled: false, events: [] });
  const [eventRegistrationStatus, setEventRegistrationStatus] = useState({ registrations: [] });
  const [reservationEligibility, setReservationEligibility] = useState(null);
  const [publicReservations, setPublicReservations] = useState([]);
  const [publicReservationsLoading, setPublicReservationsLoading] = useState(false);
  const [savingMemberReservation, setSavingMemberReservation] = useState(false);
  const [savingMemberReservationActionId, setSavingMemberReservationActionId] = useState("");
  const [publicMembershipPayments, setPublicMembershipPayments] = useState({ receipts: [], events: [], summary: {} });
  const [publicMembershipPaymentsLoading, setPublicMembershipPaymentsLoading] = useState(false);
  const [requestingQualificationKey, setRequestingQualificationKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyEventId, setBusyEventId] = useState("");
  const [busyMembershipTypeId, setBusyMembershipTypeId] = useState("");
  const [cancellingEventRegistrationId, setCancellingEventRegistrationId] = useState("");
  const [checkoutState, setCheckoutState] = useState({ status: "idle", sessionId: "" });
  const [membershipCheckoutCompletion, setMembershipCheckoutCompletion] = useState({
    status: "idle",
    lastError: "",
    claim: null,
    checkout: null
  });
  const [applicantStatus, setApplicantStatus] = useState(null);
  const [applicantStatusLoading, setApplicantStatusLoading] = useState(false);
  const [applicantStatusError, setApplicantStatusError] = useState("");
  const [acknowledgingWaiver, setAcknowledgingWaiver] = useState(false);
  const [acknowledgingGuestWaiverEntryId, setAcknowledgingGuestWaiverEntryId] = useState("");
  const [submittingPrequalification, setSubmittingPrequalification] = useState(false);
  const [submittingBackgroundCheck, setSubmittingBackgroundCheck] = useState(false);
  const [redeemingGuestPass, setRedeemingGuestPass] = useState(false);
  const [renewingMembership, setRenewingMembership] = useState(false);
  const [changingMembershipPlan, setChangingMembershipPlan] = useState(false);
  const [payingMembershipBalance, setPayingMembershipBalance] = useState(false);
  const [planChangeMembershipTypeId, setPlanChangeMembershipTypeId] = useState("");
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [prequalificationResponses, setPrequalificationResponses] = useState({});
  const [leadForm, setLeadForm] = useState(defaultLeadForm);
  const [submittingLead, setSubmittingLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [eventAutoRefreshActive, setEventAutoRefreshActive] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [unsubscribeState, setUnsubscribeState] = useState({ status: "idle", email: "", error: "" });
  const [memberPortalTab, setMemberPortalTab] = useState(() => (
    typeof window === "undefined" ? "home" : memberPortalTabFromHash(window.location.hash)
  ));
  const checkoutConfirmationSessionRef = useRef("");
  const membershipSignupPlanRef = useRef("");
  const startMembershipSignupRef = useRef(null);
  const eventsAppAvailable = Boolean(publicEvents.enabled || eventRegistrationCompletionId || eventCompletionEventId);
  const plans = context?.membership?.plans || EMPTY_LIST;
  const publicChapters = (context?.membership?.chapters?.chapters?.length > 0
    ? context.membership.chapters.chapters
    : publicChapterDirectory.chapters) || EMPTY_LIST;
  const chaptersEnabled = Boolean(
    (context?.membership?.chapters?.enabled || publicChapterDirectory.enabled) &&
    publicChapters.length > 0
  );
  const currentApplicantMemberForRouting = applicantStatus?.members?.find((item) => item.memberId === applicantStatus.current?.memberId);
  const currentApplicantIntentForRouting = applicantStatus?.signupIntents?.find((item) => item.signupIntentId === applicantStatus.current?.signupIntentId)
    || applicantStatus?.signupIntents?.[0];
  const applicantHomeChapterId = currentApplicantMemberForRouting?.homeChapterId
    || currentApplicantMemberForRouting?.chapterIds?.[0]
    || currentApplicantIntentForRouting?.homeChapterId
    || currentApplicantIntentForRouting?.chapterSnapshot?.chapterId
    || "";
  const memberConsoleChapterId = memberConsoleMode ? applicantHomeChapterId : "";
  const effectiveEventChapterId = memberConsoleMode ? memberConsoleChapterId : selectedChapterId;
  const campaignAttribution = useMemo(() => publicCampaignAttribution(context?.range), [context?.range]);
  const campaignAttributionText = campaignAttributionLabel(campaignAttribution);

  useEffect(() => {
    let cancelled = false;
    async function loadPublicRange() {
      setLoading(true);
      setError("");
      const membershipPageRequest = typeof window !== "undefined" && /\/membership\/?$/.test(window.location.pathname);
      const publicHomeRequest = typeof window !== "undefined" && /^\/r\/[^/]+\/?$/.test(window.location.pathname);
      const cachedContext = initialPublicContext(slug);
      setContext(cachedContext);

      try {
        const result = membershipPageRequest
          ? await getPublicRangeMembership(slug)
          : publicHomeRequest
            ? await getPublicRangeProfile(slug)
            : await getPublicRange(slug);
        if (publicHomeRequest && isChapterOrganization(result?.range)) {
          const chapterResult = await getPublicChapters(slug).catch(() => ({ enabled: false, chapters: [] }));
          if (!cancelled) {
            setPublicChapterDirectory({
              enabled: chapterResult.enabled !== false,
              chapters: chapterResult.chapters || []
            });
          }
        } else if (!cancelled) {
          setPublicChapterDirectory({ enabled: false, chapters: [] });
        }
        if (!cancelled) {
          setContext(result);
          writeCachedPublicContext(slug, result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPublicRange();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (typeof window === "undefined" || !chaptersEnabled || publicChapters.length === 0) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const chapterFromQuery = params.get("chapterId") || params.get("chapter") || "";
    if (chapterFromQuery && publicChapters.some((chapter) => chapter.chapterId === chapterFromQuery)) {
      setSelectedChapterId((current) => current || chapterFromQuery);
    }
  }, [chaptersEnabled, publicChapters]);

  useEffect(() => {
    if (!chaptersEnabled) {
      if (selectedChapterId) {
        setSelectedChapterId("");
      }
      return;
    }
    const applicantChapter = publicChapters.find((chapter) => chapter.chapterId === applicantHomeChapterId);
    if (applicantChapter && selectedChapterId !== applicantChapter.chapterId) {
      setSelectedChapterId(applicantChapter.chapterId);
      return;
    }
    if (!selectedChapterId || !publicChapters.some((chapter) => chapter.chapterId === selectedChapterId)) {
      const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
      const chapterFromQuery = params.get("chapterId") || params.get("chapter") || "";
      const queryChapter = publicChapters.find((chapter) => chapter.chapterId === chapterFromQuery);
      const memberChapter = publicChapters.find((chapter) => chapter.chapterId === memberConsoleChapterId);
      setSelectedChapterId(memberChapter?.chapterId || queryChapter?.chapterId || publicChapters[0]?.chapterId || "");
    }
  }, [applicantHomeChapterId, chaptersEnabled, memberConsoleChapterId, publicChapters, selectedChapterId]);

  useEffect(() => {
    if (!message) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setMessage((current) => (current === message ? "" : current));
      if (message === "Update request sent. The range has your contact information.") {
        setLeadSubmitted(false);
      }
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [message]);

  useEffect(() => {
    if (!memberConsoleMode || typeof window === "undefined") {
      return undefined;
    }

    const syncMemberPortalTab = () => {
      setMemberPortalTab(memberPortalTabFromHash(window.location.hash));
    };

    syncMemberPortalTab();
    window.addEventListener("hashchange", syncMemberPortalTab);
    return () => window.removeEventListener("hashchange", syncMemberPortalTab);
  }, [memberConsoleMode]);

  useEffect(() => {
    const publicHomeRequest = typeof window !== "undefined" && /^\/r\/[^/]+\/?$/.test(window.location.pathname);
    const shouldLoadPublicEvents = !publicHomeRequest && (
      memberConsoleMode ||
      Boolean(eventRegistrationCompletionId || eventCompletionEventId)
    );
    if (!shouldLoadPublicEvents) {
      setPublicEvents({ enabled: false, events: [] });
      return undefined;
    }

    let cancelled = false;
    async function loadPublicEvents() {
      try {
        const result = await getPublicEvents(slug, { auth: isAuthenticated, chapterId: effectiveEventChapterId });
        if (!cancelled) {
          setPublicEvents(result.events || { enabled: false, events: [] });
        }
      } catch {
        if (!cancelled) {
          setPublicEvents({ enabled: false, events: [] });
        }
      }
    }

    loadPublicEvents();
    return () => {
      cancelled = true;
    };
  }, [effectiveEventChapterId, eventCompletionEventId, eventRegistrationCompletionId, isAuthenticated, memberConsoleMode, slug]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!memberConsoleMode && params.get("checkout") === "success" && params.get("session_id")) {
      window.location.replace(`/member/r/${encodeURIComponent(slug)}?${params.toString()}`);
      return;
    }
    if (params.get("checkout") === "success") {
      setCheckoutState({
        status: "success",
        sessionId: params.get("session_id") || ""
      });
    }
    if (params.get("checkout") === "cancelled") {
      setCheckoutState({ status: "cancelled", sessionId: "" });
      setMessage("Checkout was cancelled. No payment was completed, and you can continue when you are ready.");
    }
    const signupPlanId = params.get("signup_plan");
    if (signupPlanId) {
      if (isAuthenticated) {
        const signupKey = `${slug}:${signupPlanId}`;
        if (membershipSignupPlanRef.current !== signupKey) {
          membershipSignupPlanRef.current = signupKey;
          startMembershipSignupRef.current?.(signupPlanId);
        }
      } else {
        setMessage("Sign in with ArmorySync to start this membership application.");
      }
    }
    if (params.get("event_checkout") === "success") {
      setMessage("Event checkout completed. Your event registration will update after payment confirmation.");
    }
    if (params.get("event_checkout") === "cancelled") {
      setMessage("Event checkout was cancelled. You can register again when you are ready.");
    }
    if (params.get("reservation_checkout") === "success") {
      setMessage("Reservation checkout completed. Your reservation will confirm after payment processing.");
    }
    if (params.get("reservation_checkout") === "cancelled") {
      setMessage("Reservation checkout was cancelled. Your booking is not confirmed until payment is complete.");
    }
    if (params.get("donation_checkout") === "success") {
      setMessage("Donation checkout completed. Thank you for supporting the range. Payment confirmation may take a moment to appear in the range's records.");
    }
    if (params.get("donation_checkout") === "cancelled") {
      setMessage("Donation checkout was cancelled. No donation payment was completed, and you can start again when you are ready.");
    }
  }, [isAuthenticated, memberConsoleMode, slug]);

  const loadApplicantStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setApplicantStatus(null);
      return;
    }

    setApplicantStatusLoading(true);
    setApplicantStatusError("");
    try {
      const result = await getMembershipApplicantStatus(slug);
      setApplicantStatus(result);
      return result;
    } catch (err) {
      setApplicantStatusError(err.message);
      return null;
    } finally {
      setApplicantStatusLoading(false);
    }
  }, [isAuthenticated, slug]);

  const loadEventRegistrationStatus = useCallback(async () => {
    if (!isAuthenticated || !eventsAppAvailable) {
      setEventRegistrationStatus({ registrations: [] });
      return;
    }

    try {
      const result = await getPublicEventRegistrationStatus(slug);
      setEventRegistrationStatus(result || { registrations: [] });
      return result || { registrations: [] };
    } catch {
      setEventRegistrationStatus({ registrations: [] });
      return { registrations: [] };
    }
  }, [eventsAppAvailable, isAuthenticated, slug]);

  const loadReservationEligibility = useCallback(async () => {
    if (!isAuthenticated) {
      setReservationEligibility(null);
      return;
    }

    try {
      const result = await getPublicReservationEligibility(slug);
      setReservationEligibility(result);
    } catch {
      setReservationEligibility(null);
    }
  }, [isAuthenticated, slug]);

  const loadPublicReservations = useCallback(async () => {
    if (!isAuthenticated) {
      setPublicReservations([]);
      return;
    }

    setPublicReservationsLoading(true);
    try {
      const result = await listPublicResourceReservations(slug);
      setPublicReservations(result.reservations || []);
    } catch {
      setPublicReservations([]);
    } finally {
      setPublicReservationsLoading(false);
    }
  }, [isAuthenticated, slug]);

  const handleCreatePublicReservation = useCallback(async (reservation) => {
    if (!isAuthenticated) {
      onRequireAuth({ memberIntent: "reservations", hash: "#member-dashboard" });
      return;
    }

    setSavingMemberReservation(true);
    setMessage("");
    setError("");
    try {
      const result = await createPublicResourceReservation(slug, reservation);
      if (result.nextAction === "checkout" && result.checkout?.url) {
        window.location.assign(result.checkout.url);
        return;
      }
      if (result.nextAction === "payment_not_configured") {
        setMessage("Reservation was recorded, but online payment is not configured yet. Contact the range if staff follow-up is needed.");
      } else {
        setMessage("Reservation created. It now appears in your member portal.");
      }
      if (result.reservation) {
        setPublicReservations((current) => [result.reservation, ...current]
          .sort((left, right) => String(left.startsAt || "").localeCompare(String(right.startsAt || ""))));
      }
      await Promise.all([
        loadPublicReservations(),
        loadReservationEligibility()
      ]);
      return { ok: true, reservation: result.reservation || null };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setSavingMemberReservation(false);
    }
  }, [isAuthenticated, loadPublicReservations, loadReservationEligibility, onRequireAuth, slug]);

  const handleCancelPublicReservation = useCallback(async (reservationId, options = {}) => {
    if (!isAuthenticated) {
      onRequireAuth({ memberIntent: "reservations", hash: "#member-dashboard" });
      return;
    }
    if (!reservationId) {
      return;
    }

    setSavingMemberReservationActionId(reservationId);
    setMessage("");
    setError("");
    try {
      await cancelPublicResourceReservation(slug, reservationId, options);
      setMessage("Reservation cancelled.");
      await Promise.all([
        loadPublicReservations(),
        loadReservationEligibility()
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMemberReservationActionId("");
    }
  }, [isAuthenticated, loadPublicReservations, loadReservationEligibility, onRequireAuth, slug]);

  const handleReschedulePublicReservation = useCallback(async (reservationId, reservation) => {
    if (!isAuthenticated) {
      onRequireAuth({ memberIntent: "reservations", hash: "#member-dashboard" });
      return;
    }
    if (!reservationId) {
      return;
    }

    setSavingMemberReservationActionId(reservationId);
    setMessage("");
    setError("");
    try {
      await reschedulePublicResourceReservation(slug, reservationId, reservation);
      setMessage("Reservation updated.");
      await Promise.all([
        loadPublicReservations(),
        loadReservationEligibility()
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMemberReservationActionId("");
    }
  }, [isAuthenticated, loadPublicReservations, loadReservationEligibility, onRequireAuth, slug]);

  const loadPublicMembershipPayments = useCallback(async () => {
    if (!isAuthenticated) {
      setPublicMembershipPayments({ receipts: [], events: [], summary: {} });
      return;
    }

    setPublicMembershipPaymentsLoading(true);
    try {
      const result = await getPublicMembershipPayments(slug);
      setPublicMembershipPayments(result || { receipts: [], events: [], summary: {} });
    } catch {
      setPublicMembershipPayments({ receipts: [], events: [], summary: {} });
    } finally {
      setPublicMembershipPaymentsLoading(false);
    }
  }, [isAuthenticated, slug]);

  const refreshMemberSurfaces = useCallback(async () => {
    await Promise.all([
      loadApplicantStatus(),
      loadReservationEligibility(),
      loadPublicMembershipPayments()
    ]);
  }, [loadApplicantStatus, loadPublicMembershipPayments, loadReservationEligibility]);

  const confirmReturnedMembershipCheckout = useCallback(async ({ force = false } = {}) => {
    if (checkoutState.status !== "success" || !checkoutState.sessionId || !isAuthenticated) {
      return;
    }
    if (!force && checkoutConfirmationSessionRef.current === checkoutState.sessionId) {
      return;
    }

    checkoutConfirmationSessionRef.current = checkoutState.sessionId;
    setMembershipCheckoutCompletion({ status: "confirming", lastError: "" });
    setMessage("");
    try {
      const result = await completeMembershipCheckout(slug, checkoutState.sessionId);
      if (result?.claim?.reason === "email_mismatch") {
        setMembershipCheckoutCompletion({
          status: "email_mismatch",
          lastError: "",
          claim: result.claim,
          checkout: result.checkout || null
        });
        setMessage("");
        return;
      }
      if (result?.claim?.reason === "already_claimed") {
        setMembershipCheckoutCompletion({
          status: "already_claimed",
          lastError: "",
          claim: result.claim,
          checkout: result.checkout || null
        });
        setMessage("");
        return;
      }
      setMembershipCheckoutCompletion({
        status: "confirmed",
        lastError: "",
        claim: result?.claim || null,
        checkout: result?.checkout || null
      });
      setMessage("Payment received. Your membership signup is ready for review.");
      await refreshMemberSurfaces();
      setCheckoutState({ status: "processed", sessionId: checkoutState.sessionId });
      if (memberConsoleMode && window.location.search.includes("checkout=success")) {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.hash || "#member-dashboard"}`);
      }
    } catch (err) {
      setMembershipCheckoutCompletion({
        status: "delayed",
        lastError: err.message,
        claim: null,
        checkout: null
      });
      setMessage("");
    }
  }, [
    checkoutState.sessionId,
    checkoutState.status,
    isAuthenticated,
    memberConsoleMode,
    refreshMemberSurfaces,
    slug
  ]);

  useEffect(() => {
    loadApplicantStatus();
  }, [loadApplicantStatus]);

  useEffect(() => {
    loadEventRegistrationStatus();
  }, [loadEventRegistrationStatus]);

  useEffect(() => {
    loadReservationEligibility();
  }, [loadReservationEligibility]);

  useEffect(() => {
    loadPublicReservations();
  }, [loadPublicReservations]);

  useEffect(() => {
    loadPublicMembershipPayments();
  }, [loadPublicMembershipPayments]);

  useEffect(() => {
    if (checkoutState.status !== "success" || !checkoutState.sessionId || !isAuthenticated) {
      setMembershipCheckoutCompletion({ status: "idle", lastError: "", claim: null, checkout: null });
      return undefined;
    }

    confirmReturnedMembershipCheckout();
    return undefined;
  }, [
    confirmReturnedMembershipCheckout,
    checkoutState.sessionId,
    checkoutState.status,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (checkoutState.status !== "success" || !isAuthenticated || membershipCheckoutCompletion.status !== "delayed") {
      return undefined;
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      loadApplicantStatus();
      if (attempts >= 6) {
        window.clearInterval(timer);
      }
    }, 4000);

    return () => {
      window.clearInterval(timer);
    };
  }, [checkoutState.status, isAuthenticated, loadApplicantStatus, membershipCheckoutCompletion.status]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if ((params.get("event_checkout") !== "success" && !eventRegistrationCompletionId) || !isAuthenticated) {
      setEventAutoRefreshActive(false);
      return undefined;
    }

    let attempts = 0;
    setEventAutoRefreshActive(true);
    const timer = window.setInterval(() => {
      attempts += 1;
      loadEventRegistrationStatus();
      if (attempts >= 6) {
        setEventAutoRefreshActive(false);
        window.clearInterval(timer);
      }
    }, 4000);

    return () => {
      setEventAutoRefreshActive(false);
      window.clearInterval(timer);
    };
  }, [eventRegistrationCompletionId, isAuthenticated, loadEventRegistrationStatus]);

  async function handleRegisterForEvent(eventRecord) {
    setMessage("");
    setError("");

    if (!isAuthenticated) {
      onRequireAuth({ memberIntent: "events", eventId: eventRecord.eventId, hash: "#events" });
      return;
    }

    const hadRegistrationBeforeSubmit = (eventRegistrationStatus.registrations || [])
      .some((registration) => registration.eventId === eventRecord.eventId);
    setBusyEventId(eventRecord.eventId);
    try {
      const result = await createPublicEventRegistration(slug, eventRecord.eventId);
      if (result.nextAction === "redirect_to_checkout" && result.checkout?.url) {
        window.location.assign(result.checkout.url);
        return;
      }
      if (result.nextAction === "payment_not_configured") {
        setMessage("This paid event is not ready for online checkout yet.");
        await loadEventRegistrationStatus();
        return;
      }
      const title = publicEventTitleText(
        result.registration?.eventId === eventRecord.eventId
          ? (result.event?.title || eventRecord.title)
          : eventRecord.title
      );
      const status = result.registration?.status;
      if (result.registration?.eventId) {
        setEventRegistrationStatus((current) => {
          const registrations = current?.registrations || [];
          const withoutCurrent = registrations.filter((registration) => registration.eventId !== result.registration.eventId);
          return {
            ...(current || {}),
            registrations: [result.registration, ...withoutCurrent]
          };
        });
      }
      setMessage(status === "waitlisted"
        ? `You joined the waitlist for ${title}.`
        : `You are registered for ${title}.`);
      const refreshed = await loadEventRegistrationStatus();
      if (result.registration?.eventId && !(refreshed?.registrations || []).some((registration) => registration.eventId === result.registration.eventId)) {
        setEventRegistrationStatus((current) => ({
          ...(current || {}),
          registrations: [result.registration, ...((current?.registrations || []).filter((registration) => registration.eventId !== result.registration.eventId))]
        }));
      }
    } catch (err) {
      if (/active registration/i.test(err.message || "")) {
        const refreshed = await loadEventRegistrationStatus();
        const refreshedRegistration = (refreshed?.registrations || [])
          .find((registration) => registration.eventId === eventRecord.eventId);
        if (!hadRegistrationBeforeSubmit) {
          const title = publicEventTitleText(eventRecord.title);
          setMessage(refreshedRegistration?.status === "waitlisted"
            ? `You joined the waitlist for ${title}.`
            : `You are registered for ${title}.`);
        } else {
          setMessage("You already have an active registration for this event. Review it in Classes.");
        }
        if (!refreshedRegistration) {
          const inferredRegistration = {
            eventId: eventRecord.eventId,
            status: "confirmed",
            paymentStatus: "not_required",
            source: "public",
            inferredFromDuplicate: true
          };
          setEventRegistrationStatus((current) => ({
            ...(current || {}),
            registrations: [inferredRegistration, ...((current?.registrations || []).filter((registration) => registration.eventId !== eventRecord.eventId))]
          }));
        }
      } else {
        setError(err.message);
      }
    } finally {
      setBusyEventId("");
    }
  }

  async function handleCancelEventRegistration(registration) {
    setMessage("");
    setError("");
    const reason = window.prompt("Cancellation reason (optional)");
    if (reason === null) {
      return;
    }
    setCancellingEventRegistrationId(registration.registrationId);
    try {
      const result = await cancelPublicEventRegistration(slug, registration.registrationId, { reason: reason.trim() });
      setEventRegistrationStatus(result || { registrations: [] });
      setMessage("Event registration cancelled.");
    } catch (err) {
      setError(err.message);
    } finally {
      setCancellingEventRegistrationId("");
    }
  }

  async function handleRedeemGuestPass() {
    setMessage("");
    setError("");

    if (!isAuthenticated) {
      onRequireAuth({ memberIntent: "membership", hash: "#member-status" });
      return;
    }

    const guestName = window.prompt("Guest name (optional)");
    if (guestName === null) {
      return;
    }
    const guestEmail = window.prompt("Guest email (optional)");
    if (guestEmail === null) {
      return;
    }
    const note = window.prompt("Visit note (optional)");
    if (note === null) {
      return;
    }

    setRedeemingGuestPass(true);
    try {
      await redeemPublicGuestPass(slug, {
        guestName: guestName.trim(),
        guestEmail: guestEmail.trim(),
        note: note.trim()
      });
      setMessage("Guest pass redeemed. Staff can now see the ledger entry on your member record.");
      loadApplicantStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeemingGuestPass(false);
    }
  }

  async function handleAcknowledgeGuestWaiver(entryId) {
    setMessage("");
    setError("");
    setAcknowledgingGuestWaiverEntryId(entryId);
    try {
      const result = await acknowledgePublicGuestWaiver(slug, entryId);
      setApplicantStatus(result);
      setMessage("Guest waiver acknowledged.");
    } catch (err) {
      setError(err.message);
    } finally {
      setAcknowledgingGuestWaiverEntryId("");
    }
  }

  async function handleRequestQualification(key) {
    setMessage("");
    setError("");

    if (!isAuthenticated) {
      onRequireAuth({ memberIntent: "membership", hash: "#member-status" });
      return;
    }

    setRequestingQualificationKey(key);
    try {
      await requestPublicQualification(slug, {
        key,
        label: qualificationDisplayLabel(key)
      });
      setMessage(`Qualification request submitted for ${qualificationDisplayLabel(key)}.`);
      loadApplicantStatus();
      loadReservationEligibility();
    } catch (err) {
      setError(err.message);
    } finally {
      setRequestingQualificationKey("");
    }
  }

  async function handleAcknowledgeWaiver() {
    setAcknowledgingWaiver(true);
    setApplicantStatusError("");
    try {
      const result = await acknowledgeMembershipWaiver(slug);
      setApplicantStatus(result);
      setMessage("Waiver acknowledgement saved.");
    } catch (err) {
      setApplicantStatusError(err.message);
    } finally {
      setAcknowledgingWaiver(false);
    }
  }

  function handlePrequalificationResponseChange(key, value) {
    setPrequalificationResponses((current) => ({
      ...current,
      [key]: value
    }));
  }

  function handleLeadFormChange(patch) {
    setLeadForm((current) => ({
      ...current,
      ...patch
    }));
    setLeadSubmitted(false);
  }

  async function handleCreateLead(event) {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!leadForm.email.trim()) {
      setError("Enter an email address so the range can send opening updates.");
      return;
    }

    setSubmittingLead(true);
    try {
      await createPublicLead(slug, {
        ...leadForm,
        ...campaignAttribution
      });
      setLeadSubmitted(true);
      setLeadForm({
        ...defaultLeadForm(),
        interest: leadInterestOptions[0]?.value || "opening_updates"
      });
      setMessage("Update request sent. The range has your contact information.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingLead(false);
    }
  }

  async function handleStartMembershipSignup(plan) {
    const membershipTypeId = typeof plan === "string" ? plan : plan?.membershipTypeId;
    if (!membershipTypeId) {
      setError("Choose a membership plan before starting the application.");
      return;
    }

    setMessage("");
    setError("");
    setApplicantStatusError("");

    if (!isAuthenticated) {
      onRequireAuth({
        signupPlanId: membershipTypeId,
        memberIntent: "membership",
        hash: "#member-status"
      });
      return;
    }

    setBusyMembershipTypeId(membershipTypeId);
    try {
      const result = await createMembershipSignupIntent(slug, membershipTypeId, {
        source: "public_membership_page"
      });
      if (result.nextAction === "redirect_to_checkout" && result.checkout?.url) {
        window.location.assign(result.checkout.url);
        return;
      }
      await loadApplicantStatus();
      const nextParams = new URLSearchParams(window.location.search);
      nextParams.delete("signup_plan");
      const query = nextParams.toString();
      const nextUrl = `/member/r/${encodeURIComponent(slug)}${query ? `?${query}` : ""}#member-status`;
      setMessage(
        result.nextAction === "payment_not_required"
          ? "Membership application started. Complete the review form below."
          : "Membership request started. Complete the review steps below."
      );
      window.location.assign(nextUrl);
    } catch (err) {
      setApplicantStatusError(err.message);
      setError(err.message);
    } finally {
      setBusyMembershipTypeId("");
    }
  }
  startMembershipSignupRef.current = handleStartMembershipSignup;

  async function handleRenewMembership(member) {
    setMessage("");
    setApplicantStatusError("");
    setRenewingMembership(true);
    try {
      const result = await createMembershipRenewalCheckout(slug, member.membershipTypeId);
      if (result.nextAction === "redirect_to_checkout" && result.checkout?.url) {
        window.location.assign(result.checkout.url);
        return;
      }
      if (result.nextAction === "payment_not_required") {
        setMessage("Renewal started. The range will confirm your updated membership term.");
        loadApplicantStatus();
        return;
      }
      if (result.nextAction === "payment_not_configured") {
        setMessage("Renewal request started, but online payment is not configured yet.");
        loadApplicantStatus();
        return;
      }
      setMessage("Renewal request started.");
      loadApplicantStatus();
    } catch (err) {
      setApplicantStatusError(err.message);
    } finally {
      setRenewingMembership(false);
    }
  }

  async function handlePlanChangeMembership(member) {
    setMessage("");
    setApplicantStatusError("");
    if (!planChangeMembershipTypeId) {
      setApplicantStatusError("Select a membership plan before starting Checkout.");
      return;
    }

    setChangingMembershipPlan(true);
    try {
      const result = await createMembershipPlanChangeCheckout(slug, planChangeMembershipTypeId);
      if (result.nextAction === "redirect_to_checkout" && result.checkout?.url) {
        window.location.assign(result.checkout.url);
        return;
      }
      if (result.nextAction === "payment_not_required") {
        setMessage("Plan change started. The range will confirm your updated membership.");
        loadApplicantStatus();
        return;
      }
      if (result.nextAction === "payment_not_configured") {
        setMessage("Plan change started, but online payment is not configured yet.");
        loadApplicantStatus();
        return;
      }
      setMessage("Plan change request started.");
      loadApplicantStatus();
    } catch (err) {
      setApplicantStatusError(err.message);
    } finally {
      setChangingMembershipPlan(false);
    }
  }

  async function handlePayMembershipBalance() {
    setMessage("");
    setApplicantStatusError("");
    setPayingMembershipBalance(true);
    try {
      const result = await createMembershipBalanceCheckout(slug);
      if (result.nextAction === "redirect_to_checkout" && result.checkout?.url) {
        window.location.assign(result.checkout.url);
        return;
      }
      setMessage("Remaining balance checkout started.");
      loadApplicantStatus();
    } catch (err) {
      setApplicantStatusError(err.message);
    } finally {
      setPayingMembershipBalance(false);
    }
  }

  async function handleSubmitPrequalification(event) {
    event.preventDefault();
    const member = applicantStatus?.members?.find((item) => item.memberId === applicantStatus.current?.memberId);
    const prequalification = member?.review?.requirements?.prequalification || {};
    const checklist = prequalification.checklist || [];
    const policyApplicationForm = applicantStatus?.membership?.reviewPolicy?.publicSignup?.applicationForm;
    const applicationForm = applicationFormIsPublished(prequalification.applicationForm)
      ? normalizeApplicationFormForClient(prequalification.applicationForm)
      : normalizeApplicationFormForClient(policyApplicationForm);
    const responses = checklist.map((step) => ({
      key: step.key,
      response: prequalificationResponses[step.key] || step.applicantResponse || ""
    }));
    const existingApplicationResponses = new Map((prequalification.applicationResponses || []).map((response) => [response.questionId, response]));
    const applicationResponses = applicationFormIsPublished(applicationForm)
      ? applicationForm.sections.flatMap((section) => (
          (section.questions || []).map((question) => {
            const draft = prequalificationResponses[question.questionId];
            const existing = existingApplicationResponses.get(question.questionId) || {};
            if (draft && typeof draft === "object") {
              return { questionId: question.questionId, ...draft };
            }
            return {
              questionId: question.questionId,
              value: existing.value || "",
              otherText: existing.otherText || "",
              details: existing.details || ""
            };
          })
        ))
      : [];

    setSubmittingPrequalification(true);
    setApplicantStatusError("");
    try {
      const result = await submitMembershipPrequalification(slug, responses, applicationResponses);
      setApplicantStatus(result);
      setMessage(applicationResponses.length ? "Membership application submitted." : "Prequalification responses submitted.");
    } catch (err) {
      setApplicantStatusError(err.message);
    } finally {
      setSubmittingPrequalification(false);
    }
  }

  async function handleStartBackgroundCheck(candidate) {
    setSubmittingBackgroundCheck(true);
    setApplicantStatusError("");
    try {
      const result = await startMembershipBackgroundCheck(slug, candidate);
      setApplicantStatus(result);
      setMessage("Background check submitted.");
    } catch (err) {
      setApplicantStatusError(err.message);
    } finally {
      setSubmittingBackgroundCheck(false);
    }
  }

  const range = context?.range;
  const leadInterestOptions = useMemo(() => {
    const configured = Array.isArray(range?.publicLeadSettings?.interests)
      ? range.publicLeadSettings.interests
      : [];
    const active = configured
      .filter((interest) => interest?.status !== "archived" && interest?.status !== "disabled")
      .map((interest) => ({
        value: interest.value,
        label: interest.label || interest.value
      }))
      .filter((interest) => interest.value && interest.label);
    return active.length > 0 ? active : [
      { value: "opening_updates", label: "Opening updates" },
      { value: "events_classes", label: "Events and classes" },
      { value: "general", label: "General question" }
    ];
  }, [range?.publicLeadSettings?.interests]);
  useEffect(() => {
    if (!leadInterestOptions.some((interest) => interest.value === leadForm.interest)) {
      setLeadForm((current) => ({
        ...current,
        interest: leadInterestOptions[0]?.value || "opening_updates"
      }));
    }
  }, [leadForm.interest, leadInterestOptions]);
  const branding = range?.branding || {};
  const profile = range?.profile || {};
  const libertyPublic = isLibertyLongRange(range);
  const chapterOrganization = isChapterOrganization(range);
  const workspaceLabel = workspaceNoun(range, "range");
  const memberLabel = workspaceMemberNoun(range, "member");
  const eventPluralLabel = workspaceEventNounPlural(range, "events");
  const membershipPageMode = !memberConsoleMode && typeof window !== "undefined" && /\/membership\/?$/.test(window.location.pathname);
  const aboutPageMode = !memberConsoleMode && typeof window !== "undefined" && /\/about\/?$/.test(window.location.pathname);
  const unsubscribePageMode = !memberConsoleMode && typeof window !== "undefined" && /\/unsubscribe\/?$/.test(window.location.pathname);
  const displayName = publicDisplayName(range);
  const formattedPublicAddress = formatAddress(profile.address);
  const publicAddress = libertyPublic || publicAddressLooksInternal(formattedPublicAddress) ? "" : formattedPublicAddress;
  const supportContact = publicMemberSupportContact(range);
  const publicContactEmail = libertyPublic ? "" : supportContact.email;
  const publicContactPhone = libertyPublic ? "832-425-8857" : supportContact.phone;
  const publicLogoUrl = libertyPublic ? LIBERTY_PUBLIC_LOGO_URL : branding.logoUrl;
  const memberConsoleIconUrl = branding.consoleIconUrl || publicLogoUrl;
  const publicHeroImageUrl = libertyPublic && !memberConsoleMode ? LIBERTY_PUBLIC_HERO_URL : branding.heroImageUrl;
  const publicHeroStyle = publicHeroImageUrl
    ? {
        backgroundImage: libertyPublic
          ? `linear-gradient(90deg, rgba(18, 18, 18, 0.9) 0%, rgba(18, 18, 18, 0.74) 39%, rgba(18, 18, 18, 0.28) 100%), url(${publicHeroImageUrl})`
          : `linear-gradient(90deg, rgba(17, 24, 39, 0.92), rgba(17, 24, 39, 0.64)), url(${publicHeroImageUrl})`
      }
    : undefined;
  const publicDescription = profile.description;
  const checkoutSucceeded = checkoutState.status === "success";
  const publicCheckoutParams = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
  const publicCompletionMode = Boolean(reservationCompletionId || eventRegistrationCompletionId);
  const publicCheckoutNoticeMode = !memberConsoleMode && (
    publicCheckoutParams.get("event_checkout") === "cancelled" ||
    publicCheckoutParams.get("reservation_checkout") === "cancelled" ||
    publicCheckoutParams.get("donation_checkout") === "cancelled"
  );
  const publicStatusMode = publicCompletionMode || publicCheckoutNoticeMode;
  const publicHomeMode = !memberConsoleMode && !membershipPageMode && !aboutPageMode && !unsubscribePageMode && !publicStatusMode;
  const publicMembershipMode = !memberConsoleMode && membershipPageMode;
  const publicAboutMode = !memberConsoleMode && aboutPageMode;
  const currentMember = applicantStatus?.members?.find((item) => item.memberId === applicantStatus.current?.memberId);
  const currentSignupIntent = applicantStatus?.signupIntents?.find((item) => item.signupIntentId === applicantStatus.current?.signupIntentId)
    || applicantStatus?.signupIntents?.[0];
  const currentSignupPlan = plans.find((plan) => plan.membershipTypeId === currentSignupIntent?.membershipTypeId);
  const currentMemberPlan = currentMember?.membershipPlan ||
    plans.find((plan) => plan.membershipTypeId === currentMember?.membershipTypeId) ||
    null;
  const currentMemberWithPlan = currentMember
    ? { ...currentMember, membershipPlan: currentMemberPlan || currentMember.membershipPlan }
    : null;
  const currentMemberRequirements = currentMember?.review?.requirements || {};
  const currentMemberWaiver = currentMemberRequirements.waiver || applicantStatus?.membership?.reviewPolicy?.publicSignup?.waiver || {};
  const currentMemberWaiverStatus = String(currentMemberRequirements.waiver?.status || currentMemberWaiver.status || "").trim().toLowerCase();
  const currentPolicyWaiverRequired = applicantStatus?.membership?.reviewPolicy?.publicSignup?.waiver?.required !== false;
  const currentMemberWaiverActionNeeded = Boolean(currentMember && (
    waiverRequiresSignature(currentMemberWaiver) ||
    (currentPolicyWaiverRequired && !["completed", "accepted", "approved"].includes(currentMemberWaiverStatus))
  ));
  const currentMemberReadinessMessage = waiverStatusCopy(currentMemberWaiver).actionLabel || RELEASE_FORM_ACTION_LABEL;
  const memberAccessStatusKnown = isAuthenticated && !applicantStatusLoading && Boolean(applicantStatus);
  const hasRangeMembershipRecord = Boolean(currentMember || currentSignupIntent);
  const memberPortalNeedsSignIn = memberConsoleMode && !isAuthenticated;
  const memberPortalChecking = memberConsoleMode && isAuthenticated && !memberAccessStatusKnown && applicantStatusLoading;
  const memberPortalBlocked = memberConsoleMode && memberAccessStatusKnown && !hasRangeMembershipRecord && !checkoutSucceeded;
  const showMemberPortalContent = memberConsoleMode && !memberPortalNeedsSignIn && !memberPortalChecking && !memberPortalBlocked;
  const showStatus = isAuthenticated && ((memberConsoleMode && !memberPortalBlocked && !memberPortalChecking) || checkoutSucceeded);
  const memberPortalActions = memberPortalActionSettings(range);
  const allMemberPortalTabs = [
    {
      id: "home",
      label: "Today",
      href: "#member-dashboard",
      detail: "Hours and bookings",
      icon: "T",
      iconKey: "console-home",
      workspaceCopy: "Hours, bookings, and anything needed before you arrive."
    },
    {
      id: "membership",
      label: "Plan",
      href: "#member-status",
      detail: "Plan and access",
      icon: "M",
      iconKey: "membership",
      workspaceCopy: "Plan, release form, access, and membership actions."
    },
    {
      id: "visits",
      label: "Visits",
      href: "#member-visits",
      detail: "Reservations",
      icon: "V",
      iconKey: "resources",
      workspaceCopy: "Reserve time, review areas, and check closures.",
      unavailableCopy: `This ${workspaceLabel} has not opened member booking or visit tools for this membership yet.`
    },
    {
      id: "classes",
      label: chapterOrganization ? "Classes" : "Events",
      href: "#events",
      detail: "Schedule and registrations",
      icon: chapterOrganization ? "C" : "E",
      iconKey: chapterOrganization ? "chapters" : "events",
      workspaceCopy: `${eventPluralLabel[0].toUpperCase()}${eventPluralLabel.slice(1)}, registrations, and prep.`,
      hidden: !memberPortalActions.events
    },
    {
      id: "documents",
      label: "Docs",
      href: "#member-documents",
      detail: "Forms and rules",
      icon: "D",
      iconKey: "compliance",
      workspaceCopy: "Release forms, shared files, and range materials."
    },
    {
      id: "payments",
      label: "Pay",
      href: "#member-payments",
      detail: "Due now and receipts",
      icon: "P",
      iconKey: "payments",
      workspaceCopy: "Balances, receipts, and payment help."
    },
    {
      id: "support",
      label: "Help",
      href: "#member-support",
      detail: "Help and account",
      icon: "S",
      iconKey: "communications",
      workspaceCopy: "Contact the range and track help requests."
    }
  ];
  const memberPortalTabs = allMemberPortalTabs.filter((tab) => !tab.hidden);
  const hiddenRequestedMemberPortalTab = allMemberPortalTabs.find((tab) => tab.id === memberPortalTab && tab.hidden);
  const activeMemberPortalTab = memberPortalTabs.some((tab) => tab.id === memberPortalTab) || hiddenRequestedMemberPortalTab
    ? memberPortalTab
    : "home";
  const selectedMemberPortalTab = allMemberPortalTabs.find((tab) => tab.id === activeMemberPortalTab)
    || memberPortalTabs.find((tab) => tab.id === "home")
    || memberPortalTabs[0];
  const rawMemberAccountLabel = memberAccount?.label || "";
  const safeAccountLabel = safeMemberAccountLabel(rawMemberAccountLabel);
  const devMemberAccountMasked = Boolean(rawMemberAccountLabel && safeAccountLabel !== rawMemberAccountLabel);
  const memberAccountLabel = currentMember?.fullName || safeAccountLabel || "Signed in";
  const memberAccountDetail = currentMember?.email && currentMember.email !== memberAccountLabel
    ? "Member account"
    : devMemberAccountMasked
      ? "Member account"
      : safeAccountLabel && safeAccountLabel !== memberAccountLabel
        ? safeAccountLabel
      : "";
  const registrationByEventId = new Map(
    (eventRegistrationStatus.registrations || []).map((registration) => [registration.eventId, registration])
  );
  const completedCheckoutEventRegistration = eventRegistrationCompletionId
    ? (eventRegistrationStatus.registrations || []).find((registration) => registration.registrationId === eventRegistrationCompletionId)
    : null;
  const waitlistedEventRegistrations = (eventRegistrationStatus.registrations || [])
    .filter((registration) => registration.status === "waitlisted");
  const completedCheckoutReservation = reservationCompletionId
    ? publicReservations.find((reservation) => reservation.reservationId === reservationCompletionId)
    : null;
  const hasPublicContact = Boolean(publicContactEmail || publicContactPhone || publicAddress);
  const rangeFacts = publicRangeFacts(range);
  const rangeStory = publicRangeStory(range);
  const safetyAccent = publicSafetyAccent(range);
  const publicStatusTitle = publicCheckoutNoticeMode
    ? "Checkout cancelled"
    : reservationCompletionId
      ? "Reservation confirmed"
      : publicCompletionMode
        ? "Payment received"
        : "";
  const heroDescription = publicCheckoutNoticeMode
    ? `No payment was completed for ${displayName}. You can continue when you are ready.`
    : publicCompletionMode
    ? reservationCompletionId
      ? `Reservation confirmed for ${displayName}. Check your reservation status below.`
      : `Payment received for ${displayName}. Check your registration status below.`
    : checkoutSucceeded && range
    ? `Payment received for ${displayName}.`
    : checkoutSucceeded
      ? "Payment received."
      : publicHeroDescription(
        range,
        publicDescription || (range ? `Membership information for ${displayName}.` : "Membership information for this range."),
        membershipPageMode,
        aboutPageMode
      );
  const usesSlugPublicPath = typeof window === "undefined" || /^\/(?:member\/)?r\//.test(window.location.pathname);
  const publicHomeHref = usesSlugPublicPath ? `/r/${encodeURIComponent(slug)}` : "/";
  const publicMembershipHref = usesSlugPublicPath ? `/r/${encodeURIComponent(slug)}/membership` : "/membership";
  const publicAboutHref = usesSlugPublicPath ? `/r/${encodeURIComponent(slug)}/about` : "/about";
  const memberPortalHref = `/member/r/${encodeURIComponent(slug)}`;

  useEffect(() => {
    if (!unsubscribePageMode || !slug || unsubscribeState.status !== "idle") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const email = params.get("email") || "";
    const token = params.get("token") || "";
    if (!email || !token) {
      setUnsubscribeState({ status: "error", email, error: "This unsubscribe link is missing required information." });
      return;
    }
    setUnsubscribeState({ status: "submitting", email, error: "" });
    unsubscribePublicCommunication(slug, { email, token })
      .then(() => setUnsubscribeState({ status: "done", email, error: "" }))
      .catch((err) => setUnsubscribeState({ status: "error", email, error: err.message || "This unsubscribe link could not be verified." }));
  }, [slug, unsubscribePageMode, unsubscribeState.status]);
  const publicEventsSection = publicEvents.enabled ? (
    <PublicEventsSection
      busyEventId={busyEventId}
      chapterOrganization={chapterOrganization}
      currentMember={currentMember}
      eventPluralLabel={eventPluralLabel}
      events={publicEvents.events}
      formatEventCapacity={formatEventCapacity}
      isAuthenticated={isAuthenticated}
      memberConsoleMode={memberConsoleMode}
      onRegisterForEvent={handleRegisterForEvent}
      onRequestQualification={handleRequestQualification}
      publicEventAvailabilityLabel={publicEventAvailabilityLabel}
      publicEventDetailText={publicEventDetailText}
      publicEventInstructorText={publicEventInstructorText}
      publicEventOrientationSummary={publicEventOrientationSummary}
      publicEventPriceLabel={publicEventPriceLabel}
      publicEventPrimaryActionLabel={publicEventPrimaryActionLabel}
      publicEventRegistrationStatusLabel={publicEventRegistrationStatusLabel}
      publicEventRegistrationWindowLabel={publicEventRegistrationWindowLabel}
      publicEventTitleText={publicEventTitleText}
      publicEventTypeLabel={publicEventTypeLabel}
      qualificationDisplayLabel={qualificationDisplayLabel}
      qualificationKeyList={qualificationKeyList}
      registrationByEventId={registrationByEventId}
      requestingQualificationKey={requestingQualificationKey}
      range={range}
      summarizeEventAccess={eventQualificationSummary}
    />
  ) : null;

  if (loading && !context && !error) {
    return null;
  }

  return (
    <PublicSiteShell
      branding={branding}
      chapterOrganization={chapterOrganization}
      currentMember={currentMember}
      currentMemberPlan={currentMemberPlan}
      currentSignupIntent={currentSignupIntent}
      currentSignupPlan={currentSignupPlan}
      displayName={displayName}
      heroDescription={heroDescription}
      heroKicker={publicStatusMode ? "Checkout status" : publicHeroKicker(range, memberConsoleMode, membershipPageMode, aboutPageMode)}
      isAuthenticated={isAuthenticated}
      libertyFacilityLine={libertyFacilityLine()}
      libertyPublic={libertyPublic}
      aboutPageMode={aboutPageMode}
      memberAccountDetail={memberAccountDetail}
      memberAccountLabel={memberAccountLabel}
      memberConsoleIconUrl={memberConsoleIconUrl}
      memberConsoleMode={memberConsoleMode}
      memberLabel={memberLabel}
      memberPortalBlocked={memberPortalBlocked}
      memberPortalHref={memberPortalHref}
      memberPortalNeedsSignIn={memberPortalNeedsSignIn}
      memberStatusLabel={memberStatusLabel}
      membershipPageMode={membershipPageMode}
      onMemberSignOut={onMemberSignOut}
      onRequireAuth={onRequireAuth}
      publicAboutHref={publicAboutHref}
      publicCompletionMode={publicStatusMode}
      publicHeroImageUrl={publicHeroImageUrl}
      publicHeroStyle={publicHeroStyle}
      publicHomeHref={publicHomeHref}
      publicLogoUrl={publicLogoUrl}
      publicStatusTitle={publicStatusTitle}
      rangeFacts={rangeFacts}
      safetyAccent={safetyAccent}
      showMemberPortalContent={showMemberPortalContent}
      slug={slug}
      workspaceLabel={workspaceLabel}
    >
        {!loading && error && <div className="notice is-error">{error}</div>}
        {!loading && (!error || memberConsoleMode) && (
          <>
            {publicHomeMode ? (
              <nav className="public-page-nav" aria-label="Public range sections">
                {chapterOrganization && publicChapters.length > 0 && <a href="#chapters">Chapters</a>}
                {libertyPublic && <a href={publicAboutHref}>About us</a>}
                <a href="#updates">Membership</a>
                <a href="#updates">Updates</a>
                {hasPublicContact && <a href="#contact">Contact</a>}
              </nav>
            ) : null}
            {message && (
              <div className={`notice ${checkoutState.status === "cancelled" ? "is-info" : "is-success"}`}>
                {message}
              </div>
            )}

            {unsubscribePageMode && (
              <div className="public-requirements-panel public-unsubscribe-panel">
                <div>
                  <span className="eyebrow">Email preferences</span>
                  <h3>
                    {unsubscribeState.status === "done"
                      ? "You are unsubscribed"
                      : unsubscribeState.status === "submitting"
                        ? "Updating your preference"
                        : "We could not update this link"}
                  </h3>
                  <p>
                    {unsubscribeState.status === "done"
                      ? `Member broadcast emails for ${unsubscribeState.email || "this email"} are now turned off for this range.`
                      : unsubscribeState.status === "submitting"
                        ? "RangeGrid is updating your email preference."
                        : unsubscribeState.error || "This unsubscribe link could not be verified."}
                  </p>
                </div>
                <a className="public-primary-action" href={publicHomeHref}>
                  Back to website
                </a>
              </div>
            )}

            {checkoutSucceeded && !isAuthenticated && (
              <div className="public-requirements-panel public-checkout-claim-panel">
                <div>
                  <span className="eyebrow">Payment received</span>
                  <h3>Create your member access</h3>
                  <p>
                    Sign in or create an ArmorySync account with the same email used at checkout to link this paid signup to your member console for this range.
                    After that, the range can review any waiver, orientation, or qualification steps.
                  </p>
                </div>
                <button type="button" className="public-primary-action" onClick={onRequireAuth}>
                  Continue to member access
                </button>
              </div>
            )}

            {checkoutSucceeded && isAuthenticated && membershipCheckoutCompletion.status === "email_mismatch" && (
              <div className="public-requirements-panel public-checkout-recovery-panel">
                <div>
                  <span className="eyebrow">Membership payment</span>
                  <h3>Sign in with the checkout email</h3>
                  <p>
                    This payment was made with {membershipCheckoutCompletion.claim?.checkoutEmail || "a different checkout email"}.
                    To protect member records, sign in with that email or ask the range to link the membership manually.
                  </p>
                  {membershipCheckoutCompletion.claim?.loginEmail && (
                    <small>Current signed-in email: {membershipCheckoutCompletion.claim.loginEmail}</small>
                  )}
                </div>
                <div className="public-checkout-recovery-actions">
                  <button type="button" className="secondary-button" onClick={onMemberSignOut}>
                    Sign out
                  </button>
                </div>
              </div>
            )}

            {checkoutSucceeded && isAuthenticated && membershipCheckoutCompletion.status === "already_claimed" && (
              <div className="public-requirements-panel public-checkout-recovery-panel">
                <div>
                  <span className="eyebrow">Membership payment</span>
                  <h3>This membership is already linked</h3>
                  <p>
                    This paid signup is already connected to another ArmorySync account. Ask the range to help if that is not expected.
                  </p>
                </div>
              </div>
            )}

            {checkoutSucceeded && isAuthenticated && ["confirming", "delayed"].includes(membershipCheckoutCompletion.status) && (
              <div className="public-requirements-panel public-checkout-recovery-panel">
                <div>
                  <span className="eyebrow">Membership payment</span>
                  <h3>
                    {membershipCheckoutCompletion.status === "confirming"
                      ? "Confirming your member record"
                      : "Payment received, member record still catching up"}
                  </h3>
                  <p>
                    {membershipCheckoutCompletion.status === "confirming"
                      ? "We are linking your payment to this range member portal."
                      : "Payment was received, but the member record is not fully linked yet. You can retry now or refresh your status in a moment."}
                  </p>
                  {membershipCheckoutCompletion.lastError && (
                    <small>{memberCheckoutRecoveryMessage(membershipCheckoutCompletion.lastError)}</small>
                  )}
                </div>
                <div className="public-checkout-recovery-actions">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={refreshMemberSurfaces}
                    disabled={membershipCheckoutCompletion.status === "confirming" || applicantStatusLoading}
                  >
                    Check status
                  </button>
                  <button
                    type="button"
                    className="public-primary-action"
                    onClick={() => confirmReturnedMembershipCheckout({ force: true })}
                    disabled={membershipCheckoutCompletion.status === "confirming"}
                  >
                    {membershipCheckoutCompletion.status === "confirming" ? "Confirming..." : "Retry confirmation"}
                  </button>
                </div>
              </div>
            )}

            {reservationCompletionId && (
              <div className="public-requirements-panel">
                <h3>Reservation checkout</h3>
                {!isAuthenticated ? (
                  <>
                    <p>Sign in with the same ArmorySync account to view this reservation.</p>
                    <button type="button" className="secondary-button" onClick={onRequireAuth}>
                      Sign in
                    </button>
                  </>
                ) : publicReservationsLoading ? (
                  <p>Checking reservation status...</p>
                ) : completedCheckoutReservation ? (
                  <dl>
                    <div>
                      <dt>Status</dt>
                      <dd>{publicReservationStatusLabel(completedCheckoutReservation.status)}</dd>
                    </div>
                    <div>
                      <dt>Resource</dt>
                      <dd>{completedCheckoutReservation.resource?.name || "Reserved resource"}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>
                        {formatPublicEventDate(completedCheckoutReservation.startsAt)}
                        {" - "}
                        {formatPublicEventDate(completedCheckoutReservation.endsAt)}
                      </dd>
                    </div>
                    {completedCheckoutReservation.payment?.status && (
                      <div>
                        <dt>Payment</dt>
                        <dd>{completedCheckoutReservation.payment.status}</dd>
                      </div>
                    )}
                  </dl>
                ) : (
                  <p>Checkout returned successfully. Payment confirmation may still be processing; refresh your reservations in a moment.</p>
                )}
              </div>
            )}

            {eventRegistrationCompletionId && (
              <div className="public-requirements-panel">
                <h3>Event checkout</h3>
                {!isAuthenticated ? (
                  <>
                    <p>Sign in with the same ArmorySync account to view this event registration.</p>
                    <button type="button" className="secondary-button" onClick={onRequireAuth}>
                      Sign in
                    </button>
                  </>
                ) : completedCheckoutEventRegistration ? (
                  <dl>
                    <div>
                      <dt>Status</dt>
                      <dd>{publicEventRegistrationStatusLabel(completedCheckoutEventRegistration.status)}</dd>
                    </div>
                    <div>
                      <dt>Event</dt>
                      <dd>{completedCheckoutEventRegistration.event?.title || "Registered event"}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>{completedCheckoutEventRegistration.event?.startsAt ? formatPublicEventDate(completedCheckoutEventRegistration.event.startsAt, publicEventTimezone(completedCheckoutEventRegistration.event, range)) : "Date announced by the range"}</dd>
                    </div>
                    {completedCheckoutEventRegistration.event?.location && (
                      <div>
                        <dt>Location</dt>
                        <dd>{completedCheckoutEventRegistration.event.location}</dd>
                      </div>
                    )}
                    {completedCheckoutEventRegistration.checkout?.status && (
                      <div>
                        <dt>Checkout</dt>
                        <dd>{publicEventRegistrationStatusLabel(completedCheckoutEventRegistration.status)}</dd>
                      </div>
                    )}
                  </dl>
                ) : (
                  <p>Checkout returned successfully. Payment confirmation may still be processing; refresh your event registrations in a moment.</p>
                )}
              </div>
            )}

            {!memberConsoleMode && showStatus && eventRegistrationStatus.registrations?.length > 0 && (
              <div className="public-events-panel" id="event-registrations">
                <div>
                  <h3>Your event registrations</h3>
                  <p>
                    {eventAutoRefreshActive
                      ? "Waiting for payment confirmation from Stripe."
                      : "Event registrations tied to your ArmorySync account for this range."}
                  </p>
                </div>
                {waitlistedEventRegistrations.length > 0 && (
                  <div className="public-requirements-panel">
                    <h3>Waitlist status</h3>
                    <p>
                      {waitlistedEventRegistrations.length} registration{waitlistedEventRegistrations.length === 1 ? " is" : "s are"} on the waitlist.
                      The range will confirm your spot if capacity opens.
                    </p>
                  </div>
                )}
                <div className="public-events-list">
                  {eventRegistrationStatus.registrations.map((registration) => (
                    <article key={registration.registrationId}>
                      <div>
                        <strong>{publicEventTitleText(registration.event?.title || "Registered event")}</strong>
                        <span>{registration.event?.startsAt ? formatPublicEventDate(registration.event.startsAt, publicEventTimezone(registration.event, range)) : "Date announced by the range"}</span>
                        <span>{registration.event?.location || "Location announced by the range"}</span>
                        {registrationLooksLikeOrientation(registration) && (
                          <span>
                            Orientation attendance: {publicEventAttendanceStatusLabel(registration.attendance?.status)}
                            {registration.attendance?.checkedInAt ? ` / ${formatDateOnly(registration.attendance.checkedInAt)}` : ""}
                          </span>
                        )}
                        {registration.cancellationReason && <span>Reason: {registration.cancellationReason}</span>}
                      </div>
                      <div className="public-event-actions">
                        <span className="status-badge">{publicEventRegistrationStatusLabel(registration.status)}</span>
                        {registration.checkout?.url && (
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => window.location.assign(registration.checkout.url)}
                          >
                            Continue checkout
                          </button>
                        )}
                        {!["cancelled", "attended", "no_show"].includes(registration.status) && (
                          <button
                            type="button"
                            className="secondary-button"
                            disabled={cancellingEventRegistrationId === registration.registrationId}
                            onClick={() => handleCancelEventRegistration(registration)}
                          >
                            {cancellingEventRegistrationId === registration.registrationId ? "Cancelling..." : "Cancel"}
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {(memberPortalNeedsSignIn || memberPortalChecking || memberPortalBlocked) ? (
              <MemberPortalAccessGate
                displayName={displayName}
                isAuthenticated={isAuthenticated}
                loading={memberPortalChecking}
                membershipHref={publicMembershipHref}
                workspaceLabel={workspaceLabel}
                onRequireAuth={onRequireAuth}
                onSignOut={onMemberSignOut}
              />
            ) : memberConsoleMode && showMemberPortalContent ? (
              <MemberWorkspaceShell
                activeTab={activeMemberPortalTab}
                displayName={displayName}
                member={currentMemberWithPlan}
                memberAccountDetail={memberAccountDetail}
                memberAccountLabel={memberAccountLabel}
                signupIntent={currentSignupIntent ? { ...currentSignupIntent, membershipPlan: currentSignupPlan } : null}
                selectedTab={selectedMemberPortalTab}
                tabs={memberPortalTabs}
              >
                {hiddenRequestedMemberPortalTab ? (
                  <MemberPortalUnavailablePanel
                    appDetail={hiddenRequestedMemberPortalTab.unavailableCopy}
                    appLabel={hiddenRequestedMemberPortalTab.label}
                    range={range}
                  />
                ) : (
                  <>
                    {activeMemberPortalTab === "home" && (
                      <MemberPortalDashboard
                        applicantStatus={applicantStatus}
                        eventRegistrations={eventRegistrationStatus.registrations}
                        isAuthenticated={isAuthenticated}
                        onRequireAuth={onRequireAuth}
                        publicEvents={publicEvents.events || []}
                        publicPlans={plans}
                        publicReservations={publicReservations}
                        range={range}
                        reservationBusinessHours={reservationEligibility?.businessHours}
                        reservationEligibility={reservationEligibility}
                      />
                    )}
                    {activeMemberPortalTab === "membership" && (
                      <ApplicantStatusPanel
                        id="member-status"
                        acknowledgingGuestWaiverEntryId={acknowledgingGuestWaiverEntryId}
                        acknowledgingWaiver={acknowledgingWaiver}
                        error={applicantStatusError}
                        loading={applicantStatusLoading}
                        onAcknowledgeGuestWaiver={handleAcknowledgeGuestWaiver}
                        onAcknowledgeWaiver={handleAcknowledgeWaiver}
                        onPrequalificationResponseChange={handlePrequalificationResponseChange}
                        onPlanChangeMembership={handlePlanChangeMembership}
                        onPlanChangeTargetChange={setPlanChangeMembershipTypeId}
                        onPayMembershipBalance={handlePayMembershipBalance}
                        onRefresh={loadApplicantStatus}
                        onRedeemGuestPass={handleRedeemGuestPass}
                        onRenewMembership={handleRenewMembership}
                        onSubmitPrequalification={handleSubmitPrequalification}
                        onStartBackgroundCheck={handleStartBackgroundCheck}
                        planChangeMembershipTypeId={planChangeMembershipTypeId}
                        eventRegistrations={eventRegistrationStatus.registrations}
                        prequalificationResponses={prequalificationResponses}
                        publicPlans={plans}
                        range={range}
                        redeemingGuestPass={redeemingGuestPass}
                        reservationEligibility={reservationEligibility}
                        changingMembershipPlan={changingMembershipPlan}
                        memberPortalOnly
                        payingMembershipBalance={payingMembershipBalance}
                        renewingMembership={renewingMembership}
                        submittingPrequalification={submittingPrequalification}
                        submittingBackgroundCheck={submittingBackgroundCheck}
                        statusContext={applicantStatus}
                      />
                    )}
                    {activeMemberPortalTab === "visits" && (
                      <MemberPortalVisitsPanel
                        businessHours={reservationEligibility?.businessHours}
                        loading={publicReservationsLoading}
                        member={currentMemberWithPlan}
                        onCancelReservation={handleCancelPublicReservation}
                        onCreateReservation={handleCreatePublicReservation}
                        onRescheduleReservation={handleReschedulePublicReservation}
                        range={range}
                        readinessBlocked={currentMemberWaiverActionNeeded}
                        readinessMessage={currentMemberReadinessMessage}
                        reservations={publicReservations}
                        reservationEligibility={reservationEligibility}
                        savingReservationActionId={savingMemberReservationActionId}
                        savingReservation={savingMemberReservation}
                      />
                    )}
                    {activeMemberPortalTab === "classes" && (
                      <MemberPortalEventsPanel
                        busyEventId={busyEventId}
                        eventLabel={chapterOrganization ? "Classes" : "Events"}
                        eventPluralLabel={eventPluralLabel}
                        eventRegistrations={eventRegistrationStatus.registrations}
                        events={publicEvents.events || []}
                        member={currentMemberWithPlan}
                        memberEventDisplayDescription={memberEventDisplayDescription}
                        memberEventDisplayTitle={memberEventDisplayTitle}
                        onRegisterForEvent={handleRegisterForEvent}
                        publicEventAttendanceStatusLabel={publicEventAttendanceStatusLabel}
                        publicEventAvailabilityLabel={publicEventAvailabilityLabel}
                        publicEventInstructorText={publicEventInstructorText}
                        publicEventRegistrationStatusLabel={publicEventRegistrationStatusLabel}
                        range={range}
                        registrationLooksLikeOrientation={registrationLooksLikeOrientation}
                      />
                    )}
                    {activeMemberPortalTab === "payments" && (
                      <MemberPortalPaymentsPanel
                        loading={publicMembershipPaymentsLoading}
                        member={currentMemberWithPlan}
                        memberSubscriptionSummary={memberSubscriptionSummary}
                        onPayMembershipBalance={handlePayMembershipBalance}
                        payingMembershipBalance={payingMembershipBalance}
                        payments={publicMembershipPayments}
                        qualificationDisplayLabel={qualificationDisplayLabel}
                        range={range}
                      />
                    )}
                    {activeMemberPortalTab === "documents" && (
                      <MemberPortalDocumentsPanel
                        applicantStatus={applicantStatus}
                        member={currentMemberWithPlan}
                        range={range}
                      />
                    )}
                    {activeMemberPortalTab === "support" && (
                      <MemberPortalSupportPanel
                        member={currentMemberWithPlan}
                        memberAccountDetail={memberAccountDetail}
                        memberAccountLabel={memberAccountLabel}
                        onSignOut={onMemberSignOut}
                        range={range}
                      />
                    )}
                  </>
                )}
              </MemberWorkspaceShell>
            ) : publicHomeMode ? (
              <PublicHomePage
                campaignAttributionText={campaignAttributionText}
                chapterOrganization={chapterOrganization}
                eventPluralLabel={eventPluralLabel}
                hasPublicContact={hasPublicContact}
                leadForm={leadForm}
                leadInterestOptions={leadInterestOptions}
                leadSubmitted={leadSubmitted}
                libertyPublic={libertyPublic}
                onCreateLead={handleCreateLead}
                onLeadFormChange={handleLeadFormChange}
                publicAddress={publicAddress}
                publicChapters={publicChapters}
                publicContactEmail={publicContactEmail}
                publicContactPhone={publicContactPhone}
                publicEventsSection={publicEventsSection}
                range={range}
                rangeStory={rangeStory}
                reservationResources={reservationEligibility?.resources || []}
                slug={slug}
                submittingLead={submittingLead}
                workspaceLabel={workspaceLabel}
              />
            ) : publicMembershipMode ? (
              <PublicMembershipApplicationPage
                busyMembershipTypeId={busyMembershipTypeId}
                currentMember={currentMember}
                leadFallback={(
                  <PublicMembershipInterestPage
                    currentMember={currentMember}
                    leadForm={leadForm}
                    leadInterestOptions={leadInterestOptions}
                    leadSubmitted={leadSubmitted}
                    libertyPublic={libertyPublic}
                    memberPortalHref={memberPortalHref}
                    onCreateLead={handleCreateLead}
                    onLeadFormChange={handleLeadFormChange}
                    publicContactEmail={publicContactEmail}
                    publicContactPhone={publicContactPhone}
                    range={range}
                    rangeStory={rangeStory}
                    reservationResources={reservationEligibility?.resources || []}
                    submittingLead={submittingLead}
                    workspaceLabel={workspaceLabel}
                  />
                )}
                memberPortalHref={memberPortalHref}
                onStartMembership={handleStartMembershipSignup}
                plans={plans}
                workspaceLabel={workspaceLabel}
              />
            ) : publicAboutMode ? (
              <LibertyAboutPage />
            ) : (
              null
            )}
          </>
        )}
    </PublicSiteShell>
  );
}
